import copy from "copy-to-clipboard";
import { Building2, Check, ClipboardCheck, Copy, Pencil, User as UserIcon, UserRound } from "lucide-react";
import moment from "moment";
import * as React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Svg } from "@/assets";
import { Badge, Card, Tooltip } from "@/components/ui";
import {
  VideoProjectStaffPicker,
  canUnassignStaff,
  recommendedStaffForRole,
  resolveStaffUser,
  staffDisplayName,
} from "@/components/VideoProjectStaffPicker";
import {
  VideoProjectQaBadges,
  VideoProjectRevisionBadge,
  VideoProjectStatusIcon,
  VideoProjectStatusPicker,
} from "@/components/VideoProjectStatusPicker";
import { cn } from "@/lib/utils";
import { User, VideoProject } from "@/services/bedrock";
import { extractInitials, URLS } from "@/utils";

import { mediaUrlIdentity } from "./kanbanColumnQuery";
import { KanbanColumnId, showKanbanPriorityAccent } from "./kanbanColumns";

interface VideoProjectKanbanCardProps {
  videoProject: VideoProject;
  staffUsers: User[];
  currentUserId?: number;
  disabled?: boolean;
  selected?: boolean;
  onAssigneeChange?: (project: VideoProject, userId: number | null) => void;
  onQaChange?: (project: VideoProject, userId: number | null) => void;
  onStatusChange?: (project: VideoProject, columnId: KanbanColumnId) => void;
}

const KANBAN_CARD_CLASS = "relative gap-0 overflow-hidden data-[size=sm]:py-0 ring-foreground/10";
const KANBAN_PRIORITY_ACCENT =
  "before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-[inherit] before:bg-sky-500";

const KanbanProjectThumbnail = React.memo(function KanbanProjectThumbnail({ src }: { src?: string | null }) {
  const nextSrc = src || Svg.PromptPlaceholder;
  const displaySrcRef = React.useRef(nextSrc);
  if (mediaUrlIdentity(displaySrcRef.current) !== mediaUrlIdentity(nextSrc)) {
    displaySrcRef.current = nextSrc;
  }

  return (
    <img
      src={displaySrcRef.current}
      alt=""
      className={cn("size-full", src ? "object-cover object-center" : "object-none object-center")}
    />
  );
});

function cardDateLabel(videoProject: VideoProject) {
  const date = videoProject.createdDate
    ? moment(videoProject.createdDate)
    : videoProject.statusDate
    ? moment(videoProject.statusDate)
    : null;
  if (!date) return null;

  const prefix = videoProject.createdDate ? "Created" : "Updated";
  const yearCutoff = moment().add(1, "month").startOf("month").subtract(1, "year");
  const dateFormat = date.isBefore(yearCutoff) ? "MMM D, YYYY" : "MMM D";
  return {
    label: `${prefix} ${date.format(dateFormat)}`,
    title: date.format("MMM D, YYYY [at] h:mm a"),
  };
}

function hasCountBadges(videoProject: VideoProject) {
  return (videoProject.revisionCount ?? 0) > 0 || (videoProject.qaFailureCount ?? 0) > 0;
}

function KanbanPriorityMark({ isPriority }: { isPriority: boolean }) {
  if (!isPriority) return null;
  return (
    <span title="Mine">
      <UserRound className="size-3 shrink-0 fill-sky-500 text-sky-500" aria-hidden="true" />
    </span>
  );
}

function KanbanCardId({
  isPriority,
  projectId,
  children,
}: {
  isPriority: boolean;
  projectId: number;
  children?: React.ReactNode;
}) {
  return (
    <span className="text-muted-foreground flex min-w-0 items-center gap-1 font-mono text-[11px]">
      <KanbanPriorityMark isPriority={isPriority} />#{projectId}
      {children}
    </span>
  );
}

function CoachQueueCount({
  role,
  users,
  currentUserId,
}: {
  role: VideoProject.QueueAssignment["role"];
  users: User[];
  currentUserId?: number | null;
}) {
  if (!users.length) return null;
  const isEditor = role === "EDITOR";
  const includesCurrentUser = currentUserId != null && users.some(user => user.id === currentUserId);
  const Icon = isEditor ? Pencil : ClipboardCheck;
  const label = isEditor ? "Assigned Editors" : "Assigned QA";

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <button
            type="button"
            data-kanban-interactive=""
            aria-label={`${users.length} ${label.toLowerCase()}`}
            className={cn(
              "pointer-events-auto inline-flex h-5 shrink-0 cursor-default items-center gap-0.5 rounded-full border px-1 text-[10px]",
              includesCurrentUser
                ? "border-violet-700 bg-violet-700 font-semibold text-white"
                : "border-violet-200 bg-violet-50 font-medium text-violet-700"
            )}
            onPointerDown={event => event.stopPropagation()}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <Icon className="size-2.5 shrink-0" aria-hidden="true" />
            {users.length}
          </button>
        }
      />
      <Tooltip.Content>
        <div className="border-background/20 -mx-3 -mt-1.5 border-b px-3 py-1.5 text-[10px] font-semibold tracking-wide uppercase">
          {label}
        </div>
        <ul className="space-y-0.5 my-1">
          {users.map(user => (
            <li key={user.id}>{staffDisplayName(user)}</li>
          ))}
        </ul>
      </Tooltip.Content>
    </Tooltip>
  );
}

function CoachQueueBadges({
  editors,
  qaUsers,
  currentUserId,
}: {
  editors: User[];
  qaUsers: User[];
  currentUserId?: number | null;
}) {
  if (!editors.length && !qaUsers.length) return null;
  return (
    <>
      <CoachQueueCount role="EDITOR" users={editors} currentUserId={currentUserId} />
      <CoachQueueCount role="QA" users={qaUsers} currentUserId={currentUserId} />
    </>
  );
}

function KanbanProjectMeta({
  videoProject,
  editors,
  qaUsers,
  currentUserId,
}: {
  videoProject: VideoProject;
  editors: User[];
  qaUsers: User[];
  currentUserId?: number | null;
}) {
  if (!videoProject.companyName && !videoProject.isTestProject && !editors.length && !qaUsers.length) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {videoProject.isTestProject ? (
        <Badge
          variant="outline"
          className="h-5 gap-1 border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-800 shadow-none"
          title="Test/demo project"
        >
          Demo
        </Badge>
      ) : null}
      {videoProject.companyName ? (
        <Badge
          variant="outline"
          className="text-muted-foreground h-5 max-w-full gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none"
          title={videoProject.companyName}
        >
          <Building2 className="size-2.5 shrink-0 text-orange-500" />
          <span className="truncate">{videoProject.companyName}</span>
        </Badge>
      ) : null}
      <CoachQueueBadges editors={editors} qaUsers={qaUsers} currentUserId={currentUserId} />
    </div>
  );
}

function KanbanCardDetails({
  videoProject,
  status,
  editors,
  qaUsers,
  currentUserId,
}: {
  videoProject: VideoProject;
  status: React.ReactNode;
  editors: User[];
  qaUsers: User[];
  currentUserId?: number | null;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="bg-muted size-16 shrink-0 overflow-hidden rounded-md">
        <KanbanProjectThumbnail src={videoProject.thumbnailUrl} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-start gap-1">
          {status}
          <div className="text-foreground line-clamp-2 min-w-0 pt-0.5 text-left text-sm leading-snug font-medium tracking-tight">
            {videoProject.playerName || "Untitled Player"}
          </div>
        </div>
        <KanbanProjectMeta
          videoProject={videoProject}
          editors={editors}
          qaUsers={qaUsers}
          currentUserId={currentUserId}
        />
      </div>
    </div>
  );
}

function KanbanCountBadges({ videoProject, className }: { videoProject: VideoProject; className?: string }) {
  if (!hasCountBadges(videoProject)) return null;

  return (
    <div className={cn("flex shrink-0 items-center gap-1", className)}>
      <VideoProjectRevisionBadge revisionCount={videoProject.revisionCount} />
      <VideoProjectQaBadges
        qaFailed={videoProject.qaStatus === "REJECTED"}
        qaFailureCount={videoProject.qaFailureCount}
      />
    </div>
  );
}

function overlayStaffDot(user: User | undefined) {
  const initials = user ? extractInitials(staffDisplayName(user)) : "";
  const photoUrl = user?.photoUrl;
  return (
    <span
      className={cn(
        "relative flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full text-[9px] font-medium shadow-sm",
        photoUrl ? "bg-muted" : user ? "bg-sky-600 text-white" : "bg-muted text-muted-foreground"
      )}
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="size-full object-cover" />
      ) : user ? (
        initials
      ) : (
        <UserIcon className="size-4" />
      )}
    </span>
  );
}

export const KanbanCardOverlay = React.memo(function KanbanCardOverlay({
  videoProject,
  staffUsers,
  currentUserId,
}: {
  videoProject: VideoProject;
  staffUsers: User[];
  currentUserId?: number | null;
}) {
  const assignments = videoProject.queueAssignments ?? [];
  const assignee = resolveStaffUser(videoProject.editorUserId, staffUsers, assignments);
  const qaUser = resolveStaffUser(videoProject.qaUserId, staffUsers, assignments);
  const showPriorityAccent = showKanbanPriorityAccent(videoProject, currentUserId);

  return (
    <div className="pointer-events-none cursor-grabbing origin-center rotate-3 scale-105 opacity-80 shadow-xl">
      <Card
        size="sm"
        className={cn(
          KANBAN_CARD_CLASS,
          showPriorityAccent && KANBAN_PRIORITY_ACCENT,
          videoProject.needsAck && "ring-amber-500"
        )}
      >
        <div className="relative z-10 flex flex-col gap-2 p-2.5">
          <div className="flex items-center justify-between gap-2">
            <KanbanCardId isPriority={Boolean(videoProject.isPriority)} projectId={videoProject.id} />
            <div className="flex shrink-0 items-center gap-1.5">
              {overlayStaffDot(assignee)}
              {overlayStaffDot(qaUser)}
            </div>
          </div>
          <KanbanCardDetails
            videoProject={videoProject}
            editors={recommendedStaffForRole(videoProject, "EDITOR", staffUsers)}
            qaUsers={recommendedStaffForRole(videoProject, "QA", staffUsers)}
            currentUserId={currentUserId}
            status={
              <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center">
                <VideoProjectStatusIcon status={videoProject.status} />
              </span>
            }
          />
          <KanbanCountBadges videoProject={videoProject} className="self-end" />
        </div>
      </Card>
    </div>
  );
});

export const VideoProjectKanbanCard = React.memo(function VideoProjectKanbanCard({
  videoProject,
  staffUsers,
  currentUserId,
  disabled = false,
  selected = false,
  onAssigneeChange,
  onQaChange,
  onStatusChange,
}: VideoProjectKanbanCardProps) {
  const [copiedId, setCopiedId] = React.useState(false);
  const assignments = videoProject.queueAssignments ?? [];
  const assignee = resolveStaffUser(videoProject.editorUserId, staffUsers, assignments);
  const qaUser = resolveStaffUser(videoProject.qaUserId, staffUsers, assignments);
  const recommendedEditors = recommendedStaffForRole(videoProject, "EDITOR", staffUsers);
  const recommendedQa = recommendedStaffForRole(videoProject, "QA", staffUsers);
  const dateLabel = cardDateLabel(videoProject);
  const showPriorityAccent = showKanbanPriorityAccent(videoProject, currentUserId);

  const handleCopyId = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!copy(String(videoProject.id))) return;
    toast.success("Project ID copied to clipboard");
    setCopiedId(true);
    window.setTimeout(() => setCopiedId(false), 1500);
  };

  return (
    <Card
      size="sm"
      disabled={disabled}
      data-selected={selected ? "" : undefined}
      className={cn(
        KANBAN_CARD_CLASS,
        "transition-[box-shadow,ring-color,ring-width] duration-200",
        selected ? "z-10 shadow-md ring-2 ring-ring/50" : "hover:shadow-md hover:ring-foreground/20",
        showPriorityAccent && KANBAN_PRIORITY_ACCENT,
        videoProject.needsAck && (selected ? "ring-2 ring-amber-500" : "ring-amber-500")
      )}
    >
      <Link
        to={URLS.editor.videoProjects.detail.replace(":id", `${videoProject.id}`)}
        aria-label={videoProject.playerName || `Video project ${videoProject.id}`}
        className={cn("absolute inset-0 z-0", disabled && "pointer-events-none")}
      />
      <div className="relative z-10 flex flex-col gap-2 p-2.5 pointer-events-none">
        <div className="flex items-center justify-between gap-2">
          <KanbanCardId isPriority={Boolean(videoProject.isPriority)} projectId={videoProject.id}>
            <button
              type="button"
              onPointerDown={event => event.stopPropagation()}
              onClick={handleCopyId}
              className="pointer-events-auto text-muted-foreground hover:text-foreground -m-0.5 shrink-0 cursor-pointer rounded p-0.5 hover:bg-muted"
              title={copiedId ? "Copied" : "Copy project ID"}
              aria-label={copiedId ? "Project ID copied" : "Copy project ID"}
            >
              {copiedId ? (
                <Check className="size-3 text-emerald-600" strokeWidth={1.5} />
              ) : (
                <Copy className="size-3" strokeWidth={1.5} />
              )}
            </button>
          </KanbanCardId>
          <div className="flex shrink-0 items-center gap-1.5">
            <VideoProjectStaffPicker
              role="EDITOR"
              compact
              selectedUserId={videoProject.editorUserId}
              selectedUser={assignee}
              otherRoleUserId={videoProject.qaUserId}
              recommendedUsers={recommendedEditors}
              staffUsers={staffUsers}
              currentUserId={currentUserId}
              disabled={disabled}
              allowUnassign={canUnassignStaff("EDITOR", videoProject.status)}
              onSelect={userId => onAssigneeChange?.(videoProject, userId)}
            />
            <VideoProjectStaffPicker
              role="QA"
              compact
              selectedUserId={videoProject.qaUserId}
              selectedUser={qaUser}
              otherRoleUserId={videoProject.editorUserId}
              recommendedUsers={recommendedQa}
              staffUsers={staffUsers}
              currentUserId={currentUserId}
              disabled={disabled}
              allowUnassign={canUnassignStaff("QA", videoProject.status)}
              onSelect={userId => onQaChange?.(videoProject, userId)}
            />
          </div>
        </div>

        <KanbanCardDetails
          videoProject={videoProject}
          editors={recommendedEditors}
          qaUsers={recommendedQa}
          currentUserId={currentUserId}
          status={
            <VideoProjectStatusPicker
              status={videoProject.status}
              statusDate={videoProject.statusDate}
              archived={!!videoProject.archived}
              requiresQa={videoProject.requiresQa}
              currentUserId={currentUserId}
              disabled={disabled}
              onSelect={columnId => onStatusChange?.(videoProject, columnId)}
            />
          }
        />

        {dateLabel || hasCountBadges(videoProject) ? (
          <div className="flex items-center justify-between gap-2">
            {dateLabel ? (
              <div
                className="text-muted-foreground pointer-events-auto min-w-0 truncate text-[11px]"
                title={dateLabel.title}
              >
                {dateLabel.label}
              </div>
            ) : null}
            <KanbanCountBadges videoProject={videoProject} className="pointer-events-auto ml-auto" />
          </div>
        ) : null}
      </div>
    </Card>
  );
});
